import React from "react";

import FoundryPageShell from "../components/foundry/FoundryPageShell";

type CarrierStatus = "research" | "active" | "blocked" | "planned";

type Carrier = {
  id: string;
  name: string;
  status: CarrierStatus;
  protocols: string[];
  messageGroups: string[];
  documents: number;
  lastActivity?: string;
};

const initialCarriers: Carrier[] = [
  {
    id: "hapag-lloyd",
    name: "Hapag-Lloyd",
    status: "research",
    protocols: ["EDI", "SFTP"],
    messageGroups: ["Booking", "BL", "Tracking", "Schedules"],
    documents: 0,
    lastActivity: "Nog niet gekoppeld",
  },
  {
    id: "maersk",
    name: "Maersk",
    status: "planned",
    protocols: ["API", "EDI"],
    messageGroups: ["Booking", "BL", "Tracking", "Schedules"],
    documents: 0,
    lastActivity: "Nog niet gekoppeld",
  },
  {
    id: "msc",
    name: "MSC",
    status: "planned",
    protocols: ["EDI", "SFTP"],
    messageGroups: ["Booking", "BL", "Tracking", "Schedules"],
    documents: 0,
    lastActivity: "Nog niet gekoppeld",
  },
];

const statusLabel: Record<CarrierStatus, string> = {
  research: "Onderzoek",
  active: "Actief",
  blocked: "Geblokkeerd",
  planned: "Gepland",
};

const statusClass: Record<CarrierStatus, string> = {
  research: "bg-amber-100 text-amber-800 border-amber-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  blocked: "bg-red-100 text-red-800 border-red-200",
  planned: "bg-slate-100 text-slate-700 border-slate-200",
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function CarrierIntegrationsPage() {
  const [carrierList, setCarrierList] = React.useState<Carrier[]>(initialCarriers);
  const [selectedCarrierId, setSelectedCarrierId] = React.useState(
    initialCarriers[0]?.id ?? ""
  );
  const [newCarrierName, setNewCarrierName] = React.useState("");

  const selectedCarrier =
    carrierList.find((carrier) => carrier.id === selectedCarrierId) ??
    carrierList[0];

  const addCarrier = () => {
    const name = newCarrierName.trim();

    if (!name) return;

    const baseId = slugify(name);
    const id = carrierList.some((carrier) => carrier.id === baseId)
      ? `${baseId}-${Date.now()}`
      : baseId;

    const newCarrier: Carrier = {
      id,
      name,
      status: "research",
      protocols: ["EDI", "SFTP"],
      messageGroups: ["Booking", "BL", "Tracking", "Schedules"],
      documents: 0,
      lastActivity: "Net toegevoegd",
    };

    setCarrierList((current) => [...current, newCarrier]);
    setSelectedCarrierId(id);
    setNewCarrierName("");
  };

  return (
    <FoundryPageShell
      fullHeight
      flush
      sidebarWidth="w-[360px]"
      inspectorWidth="w-[420px]"
      sidebar={
        <aside className="h-full border-r border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
          <div className="border-b border-slate-200 p-5 dark:border-white/10">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">
              Foundry
            </p>
            <h1 className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
              Carrier Integrations
            </h1>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Beheer rederijen, berichtsoorten, documentatie en connectorstatus.
            </p>
          </div>

          <div className="border-b border-slate-200 p-4 dark:border-white/10">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Nieuwe carrier
            </label>

            <div className="mt-2 flex gap-2">
              <input
                value={newCarrierName}
                onChange={(event) => setNewCarrierName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    addCarrier();
                  }
                }}
                placeholder="Bijv. Evergreen"
                className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-orange-400 dark:border-white/10 dark:bg-slate-900 dark:text-white"
              />

              <button
                type="button"
                onClick={addCarrier}
                className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-400"
              >
                Toevoegen
              </button>
            </div>
          </div>

          <div className="space-y-2 p-4">
            {carrierList.map((carrier) => {
              const active = carrier.id === selectedCarrier?.id;

              return (
                <button
                  key={carrier.id}
                  type="button"
                  onClick={() => setSelectedCarrierId(carrier.id)}
                  className={[
                    "w-full rounded-2xl border p-4 text-left transition",
                    active
                      ? "border-orange-300 bg-orange-50 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/10"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:hover:bg-slate-800",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-slate-950 dark:text-white">
                        {carrier.name}
                      </h2>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {carrier.protocols.join(" + ")}
                      </p>
                    </div>

                    <span
                      className={[
                        "rounded-full border px-2.5 py-1 text-xs font-medium",
                        statusClass[carrier.status],
                      ].join(" ")}
                    >
                      {statusLabel[carrier.status]}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {carrier.messageGroups.map((messageGroup) => (
                      <span
                        key={messageGroup}
                        className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-white/10 dark:text-slate-300"
                      >
                        {messageGroup}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      }
      main={
        <main className="h-full overflow-auto bg-slate-50 dark:bg-slate-950">
          <div className="border-b border-slate-200 bg-white px-8 py-6 dark:border-white/10 dark:bg-slate-900">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Geselecteerde rederij
            </p>
            <h2 className="mt-1 text-2xl font-semibold text-slate-950 dark:text-white">
              {selectedCarrier?.name}
            </h2>
            <p className="mt-2 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
              Dit wordt de businessgerichte catalogus. Collega’s zien later alleen
              wat er per shipment gebeurt; techniek blijft in beheer van jou of je
              team.
            </p>
          </div>

          <div className="grid gap-5 p-8 xl:grid-cols-2">
            {selectedCarrier?.messageGroups.map((messageGroup) => (
              <section
                key={messageGroup}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
                      {messageGroup}
                    </h3>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Concept berichtgroep vanuit carrier-documentatie.
                    </p>
                  </div>

                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                    Concept
                  </span>
                </div>

                <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Nog geen technische berichten gevonden
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Upload straks EDI-specificaties, Swagger/OpenAPI, XSD of PDF’s
                    zodat Foundry deze groep automatisch kan vullen.
                  </p>
                </div>
              </section>
            ))}
          </div>
        </main>
      }
      inspector={
        <aside className="h-full border-l border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
          <div className="border-b border-slate-200 p-5 dark:border-white/10">
            <h2 className="text-lg font-semibold text-slate-950 dark:text-white">
              Integration beheer
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Alleen zichtbaar voor beheerteam.
            </p>
          </div>

          <div className="space-y-5 p-5">
            <section className="rounded-2xl border border-slate-200 p-4 dark:border-white/10">
              <h3 className="font-semibold text-slate-950 dark:text-white">
                Documentatie
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {selectedCarrier?.documents ?? 0} documenten gekoppeld.
              </p>

              <button
                type="button"
                className="mt-4 w-full rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Document uploaden
              </button>
            </section>

            <section className="rounded-2xl border border-slate-200 p-4 dark:border-white/10">
              <h3 className="font-semibold text-slate-950 dark:text-white">
                Protocollen
              </h3>

              <div className="mt-3 flex flex-wrap gap-2">
                {selectedCarrier?.protocols.map((protocol) => (
                  <span
                    key={protocol}
                    className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700 dark:bg-white/10 dark:text-slate-300"
                  >
                    {protocol}
                  </span>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 p-4 dark:border-white/10">
              <h3 className="font-semibold text-slate-950 dark:text-white">
                Laatste activiteit
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {selectedCarrier?.lastActivity}
              </p>
            </section>
          </div>
        </aside>
      }
    />
  );
}